import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import {
  StampDiscountRateOption,
  StampDiscountUserOption,
  StampGroupDetail,
  StampGroupLineRow,
  StampGroupLogRow,
  StampGroupMemberRow,
} from '../../../../../core/models/parking/stamp-discount.model';
import { ApiStampDiscountService } from '../../../../../core/services/api/parking/api-stamp-discount.service';
import { MapperStampDiscountService } from '../../../../../core/mappers/parking/mapper-stamp-discount.service';
import { MapperParkingMasterDataService } from '../../../../../core/mappers/system-settings/mapper-parking-master-data.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import {
  PssSearchablePickerComponent,
  SearchablePickerOption,
} from '../../../../../tools/template/pss/pss-searchable-picker/pss-searchable-picker.component';
import { PssConfirmModalComponent } from '../../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { ModalAdminActivityHistoryComponent } from '../../system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';

type TabKey = 'general' | 'lines' | 'members';
type ConfirmKind = 'deactivate-line' | 'activate-line' | 'deactivate-member';

@Component({
  selector: 'app-modal-stamp-group',
  standalone: true,
  imports: [CommonModule, FormsModule, PssSearchablePickerComponent, PssConfirmModalComponent, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-stamp-group.component.html',
  styleUrl: './modal-stamp-group.component.css',
})
export class ModalStampGroupComponent implements OnInit, OnDestroy {
  private static readonly PREVIEW = 20;

  @Input() groupId = 0;
  @Input() siteId = 0;
  @Input() siteName = '';
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  readonly tabs: { key: TabKey; label: string }[] = [
    { key: 'general', label: 'ทั่วไป' },
    { key: 'lines', label: 'ส่วนลด' },
    { key: 'members', label: 'สมาชิก' },
  ];

  readonly userPageSizeOptions = [10, 20, 50];

  activeTab: TabKey = 'general';
  detail!: StampGroupDetail;
  rateOptions: StampDiscountRateOption[] = [];
  logs: StampGroupLogRow[] = [];
  logsTotal = 0;

  lineForm = { parkingRateId: 0, active: true, sequence: null as number | null };
  selectedRateLabel = '';

  selectedRateIds = new Set<number>();
  rateSearchText$ = '';

  isLoading = signal(false);
  isSaving = signal(false);

  customerPickerOptions = signal<SearchablePickerOption[]>([]);
  customerPickerTotal = signal(0);
  customerPickerLoading = signal(false);
  private customerSearch$ = new Subject<string>();

  ratePickerOptions = signal<SearchablePickerOption[]>([]);
  ratePickerTotal = signal(0);
  private rateSearchText = '';

  showUserPicker = false;
  userPickerRows: StampDiscountUserOption[] = [];
  userPickerTotal = 0;
  userPickerPage = 1;
  userPickerPageSize = 20;
  userPickerSearch = '';
  userPickerLoading = signal(false);
  private userSearch$ = new Subject<string>();

  showConfirm = false;
  confirmKind: ConfirmKind = 'deactivate-line';
  confirmTitle = '';
  confirmMessage = '';
  confirmHighlight = '';
  confirmMessageSuffix = '';
  confirmLabel = 'ยืนยัน';
  confirmIcon = 'toggle_off';
  confirmVariant: 'danger' | 'warning' = 'warning';
  private pendingLine: StampGroupLineRow | null = null;
  private pendingMember: StampGroupMemberRow | null = null;

  private readonly destroy$ = new Subject<void>();

  private readonly api = inject(ApiStampDiscountService);
  private readonly mapper = inject(MapperStampDiscountService);
  private readonly masterMapper = inject(MapperParkingMasterDataService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  get isEdit(): boolean { return this.groupId > 0 || this.detail?.id > 0; }

  get isBillable(): boolean { return this.detail?.costType === 'billable'; }
  get groupHasQuota(): boolean { return !!this.detail?.hasLimit; }

  get userPickerTotalPages(): number {
    return this.userPickerTotal === 0
      ? 0
      : Math.ceil(this.userPickerTotal / this.userPickerPageSize);
  }

  get userPickerStartIndex(): number {
    if (this.userPickerTotal === 0) return 0;
    return (this.userPickerPage - 1) * this.userPickerPageSize + 1;
  }

  get userPickerEndIndex(): number {
    return Math.min(this.userPickerPage * this.userPickerPageSize, this.userPickerTotal);
  }

  ngOnInit(): void {
    this.detail = this.emptyDetail();
    this.customerSearch$
      .pipe(debounceTime(250), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((q) => this.loadCustomerOptions(q));
    this.userSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((q) => {
        this.userPickerSearch = q;
        this.userPickerPage = 1;
        this.loadUserPickerRows();
      });

    this.loadRateOptions();
    if (this.groupId > 0) this.loadDetail();
    else {
      this.detail.siteId = this.siteId || null;
      this.detail.siteName = this.siteName;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onClose(): void { this.close.emit(); }

  setTab(tab: TabKey): void {
    if (tab !== 'general' && !this.isEdit) {
      this.toast.error('บันทึกข้อมูลทั่วไปก่อน');
      return;
    }
    this.activeTab = tab;
  }

  onCostTypeChange(): void {
    if (this.detail.costType !== 'billable') {
      this.onCustomerCleared();
      return;
    }
    this.loadCustomerOptions('');
  }

  onCustomerOpened(): void {
    this.loadCustomerOptions('');
  }

  onCustomerSearch(text: string): void {
    this.customerSearch$.next(text.trim());
  }

  onCustomerSelected(opt: SearchablePickerOption): void {
    this.detail.cpCustomerId = opt.id;
    this.detail.cpCustomerName = opt.label;
  }

  onCustomerCleared(): void {
    this.detail.cpCustomerId = null;
    this.detail.cpCustomerName = null;
  }

  onRateOpened(): void {
    this.rateSearchText = '';
    this.refreshRatePickerOptions();
  }

  onRateSearch(text: string): void {
    this.rateSearchText = text.trim().toLowerCase();
    this.refreshRatePickerOptions();
  }

  onRateSelected(opt: SearchablePickerOption): void {
    this.lineForm.parkingRateId = opt.id;
    this.selectedRateLabel = opt.label;
  }

  onRateCleared(): void {
    this.lineForm.parkingRateId = 0;
    this.selectedRateLabel = '';
  }

  openUserPicker(): void {
    if (!this.detail.id) {
      this.toast.error('บันทึกกลุ่มก่อน');
      return;
    }
    this.showUserPicker = true;
    this.userPickerPage = 1;
    this.userPickerSearch = '';
    this.loadUserPickerRows();
  }

  closeUserPicker(): void {
    this.showUserPicker = false;
  }

  onUserPickerSearchInput(value: string): void {
    this.userSearch$.next(value.trim());
  }

  onUserPageSizeChange(size: number): void {
    this.userPickerPageSize = size;
    this.userPickerPage = 1;
    this.loadUserPickerRows();
  }

  userPickerPrev(): void {
    if (this.userPickerPage <= 1) return;
    this.userPickerPage -= 1;
    this.loadUserPickerRows();
  }

  userPickerNext(): void {
    if (this.userPickerPage >= this.userPickerTotalPages) return;
    this.userPickerPage += 1;
    this.loadUserPickerRows();
  }

  selectUserFromPicker(user: StampDiscountUserOption): void {
    // Keep modal open so admin can add multiple users in one session.
    this.saveMember(user.id);
  }

  askDeactivateLine(line: StampGroupLineRow): void {
    this.pendingLine = line;
    this.pendingMember = null;
    this.confirmKind = 'deactivate-line';
    this.confirmTitle = 'ปิดส่วนลดในกลุ่ม?';
    this.confirmMessage = 'ปิดส่วนลด';
    this.confirmHighlight = `${line.rateId ?? ''} ${line.rateName ?? ''}`.trim();
    this.confirmMessageSuffix = 'โดยไม่ลบออกจากกลุ่ม — สามารถเปิดใช้อีกครั้งได้';
    this.confirmLabel = 'ปิดใช้งาน';
    this.confirmIcon = 'toggle_off';
    this.confirmVariant = 'warning';
    this.showConfirm = true;
  }

  askActivateLine(line: StampGroupLineRow): void {
    this.pendingLine = line;
    this.pendingMember = null;
    this.confirmKind = 'activate-line';
    this.confirmTitle = 'เปิดใช้ส่วนลด?';
    this.confirmMessage = 'เปิดใช้ส่วนลด';
    this.confirmHighlight = `${line.rateId ?? ''} ${line.rateName ?? ''}`.trim();
    this.confirmMessageSuffix = 'ในกลุ่มนี้อีกครั้ง';
    this.confirmLabel = 'เปิดใช้งาน';
    this.confirmIcon = 'toggle_on';
    this.confirmVariant = 'warning';
    this.showConfirm = true;
  }

  askDeactivateMember(member: StampGroupMemberRow): void {
    this.pendingMember = member;
    this.pendingLine = null;
    this.confirmKind = 'deactivate-member';
    this.confirmTitle = 'ยกเลิกสมาชิก?';
    this.confirmMessage = 'ยกเลิกสมาชิก';
    this.confirmHighlight = member.login || `#${member.targetUserId}`;
    this.confirmMessageSuffix = 'ออกจากกลุ่ม (เปลี่ยนสถานะ ไม่ลบถาวร)';
    this.confirmLabel = 'ยกเลิกสมาชิก';
    this.confirmIcon = 'person_off';
    this.confirmVariant = 'danger';
    this.showConfirm = true;
  }

  closeConfirm(): void {
    this.showConfirm = false;
    this.pendingLine = null;
    this.pendingMember = null;
  }

  onConfirmAction(): void {
    if (this.confirmKind === 'deactivate-line' && this.pendingLine) {
      this.updateLineActive(this.pendingLine, false);
    } else if (this.confirmKind === 'activate-line' && this.pendingLine) {
      this.updateLineActive(this.pendingLine, true);
    } else if (this.confirmKind === 'deactivate-member' && this.pendingMember) {
      this.deactivateMember(this.pendingMember);
    }
    this.closeConfirm();
  }

  onSaveGeneral(): void {
    if (!this.detail.name?.trim()) { this.toast.error('กรุณาระบุชื่อกลุ่ม'); return; }
    if (this.detail.costType === 'billable' && !this.detail.cpCustomerId) {
      this.toast.error('กรุณาเลือกลูกค้าสำหรับเรียกเก็บตามหลัง'); return;
    }
    const { userId, menuId } = this.auth();
    if (!userId || !menuId) return;

    this.isSaving.set(true);
    this.api.saveStampGroup({
      userId,
      menuId,
      id: this.detail.id,
      name: this.detail.name.trim(),
      siteId: this.detail.siteId,
      active: this.detail.active,
      costType: this.detail.costType,
      cpCustomerId: this.detail.costType === 'billable' ? this.detail.cpCustomerId : null,
      hasLimit: this.detail.hasLimit,
      limitType: this.detail.hasLimit ? this.detail.limitType : null,
      limitQty: this.detail.hasLimit ? this.detail.limitQty : null,
      remark: this.detail.remark,
    }).subscribe({
      next: (res) => this.handleSaveResponse(res, () => {
        const wasCreate = this.detail.id === 0;
        if (wasCreate) {
          const mapped = this.masterMapper.mapActionResponse(res);
          if (mapped.idReturn > 0) {
            this.detail.id = mapped.idReturn;
            this.groupId = mapped.idReturn;
          }
        }
        this.saveSuccess.emit();
        if (wasCreate && this.detail.id > 0) {
          this.refreshRatePickerOptions();
          this.activeTab = 'lines';
        } else {
          this.loadDetail(false);
        }
        this.loadLogs();
      }),
      error: (err) => { this.isSaving.set(false); this.toast.error(err?.error?.message || 'บันทึกไม่สำเร็จ'); },
    });
  }

  get availableRatesForCheckbox(): StampDiscountRateOption[] {
    const used = new Set(this.detail?.lines?.map((l) => l.parkingRateId) ?? []);
    const q = this.rateSearchText$.trim().toLowerCase();
    return this.rateOptions
      .filter((r) => !used.has(r.id))
      .filter((r) => {
        if (!q) return true;
        const label = `${r.rateId ?? ''} ${r.name ?? ''}`.toLowerCase();
        return label.includes(q);
      });
  }

  toggleRateCheckbox(rateId: number): void {
    if (this.selectedRateIds.has(rateId)) {
      this.selectedRateIds.delete(rateId);
    } else {
      this.selectedRateIds.add(rateId);
    }
  }

  selectAllAvailableRates(): void {
    const available = this.availableRatesForCheckbox;
    const allSelected = available.every((r) => this.selectedRateIds.has(r.id));
    if (allSelected) {
      available.forEach((r) => this.selectedRateIds.delete(r.id));
    } else {
      available.forEach((r) => this.selectedRateIds.add(r.id));
    }
  }

  get allAvailableSelected(): boolean {
    const available = this.availableRatesForCheckbox;
    return available.length > 0 && available.every((r) => this.selectedRateIds.has(r.id));
  }

  onSaveLinesBulk(): void {
    if (!this.detail.id) { this.toast.error('บันทึกกลุ่มก่อน'); return; }
    if (this.selectedRateIds.size === 0) { this.toast.error('เลือกส่วนลดอย่างน้อย 1 รายการ'); return; }
    const { userId, menuId } = this.auth();
    if (!userId || !menuId) return;
    this.isSaving.set(true);
    this.api.saveStampGroupLinesBulk({
      userId, menuId, groupId: this.detail.id,
      parkingRateIds: Array.from(this.selectedRateIds),
    }).subscribe({
      next: (res) => this.handleSaveResponse(res, () => {
        this.selectedRateIds.clear();
        this.rateSearchText$ = '';
        this.loadDetail();
        this.loadLogs();
      }),
      error: (err) => { this.isSaving.set(false); this.toast.error(err?.error?.message || 'บันทึกส่วนลดไม่สำเร็จ'); },
    });
  }

  onSaveLine(): void {
    if (!this.detail.id) { this.toast.error('บันทึกกลุ่มก่อน'); return; }
    if (!this.lineForm.parkingRateId) { this.toast.error('เลือกส่วนลด'); return; }
    if (this.detail.lines.some((l) => l.parkingRateId === this.lineForm.parkingRateId)) {
      this.toast.error('ส่วนลดนี้อยู่ในกลุ่มแล้ว'); return;
    }
    const { userId, menuId } = this.auth();
    if (!userId || !menuId) return;
    this.isSaving.set(true);
    this.api.saveStampGroupLine({
      userId, menuId, id: 0, groupId: this.detail.id,
      parkingRateId: this.lineForm.parkingRateId,
      active: this.lineForm.active,
      sequence: this.lineForm.sequence,
    }).subscribe({
      next: (res) => this.handleSaveResponse(res, () => {
        this.lineForm.parkingRateId = 0;
        this.selectedRateLabel = '';
        this.loadDetail();
        this.loadLogs();
      }),
      error: (err) => { this.isSaving.set(false); this.toast.error(err?.error?.message || 'บันทึก line ไม่สำเร็จ'); },
    });
  }

  resetMemberQuota(member: StampGroupMemberRow): void {
    const { userId, menuId } = this.auth();
    if (!userId || !menuId) return;
    this.api.resetStampGroupMemberQuota({ userId, menuId, memberId: member.id, remark: null }).subscribe({
      next: (res) => {
        const mapped = this.masterMapper.mapActionResponse(res);
        if (mapped.isSuccess) {
          this.toast.success('รีเซ็ตโควต้าแล้ว');
          this.loadDetail();
          this.loadLogs();
        } else this.toast.error(mapped.message);
      },
      error: () => this.toast.error('รีเซ็ตไม่สำเร็จ'),
    });
  }

  private updateLineActive(line: StampGroupLineRow, active: boolean): void {
    const { userId, menuId } = this.auth();
    if (!userId || !menuId) return;
    this.isSaving.set(true);
    this.api.saveStampGroupLine({
      userId,
      menuId,
      id: line.id,
      groupId: this.detail.id,
      parkingRateId: line.parkingRateId,
      active,
      sequence: line.sequence,
    }).subscribe({
      next: (res) => this.handleSaveResponse(res, () => {
        this.loadDetail();
        this.loadLogs();
      }),
      error: (err) => {
        this.isSaving.set(false);
        this.toast.error(err?.error?.message || 'อัปเดตส่วนลดไม่สำเร็จ');
      },
    });
  }

  private deactivateMember(member: StampGroupMemberRow): void {
    const { userId, menuId } = this.auth();
    if (!userId || !menuId) return;
    this.isSaving.set(true);
    this.api.saveStampGroupMember({
      userId,
      menuId,
      id: member.id,
      groupId: this.detail.id,
      targetUserId: member.targetUserId,
      active: false,
      hasLimit: member.hasLimit,
      limitType: member.limitType,
      limitQty: member.limitQty,
    }).subscribe({
      next: (res) => this.handleSaveResponse(res, () => {
        this.loadDetail();
        this.loadLogs();
      }),
      error: (err) => {
        this.isSaving.set(false);
        this.toast.error(err?.error?.message || 'ยกเลิกสมาชิกไม่สำเร็จ');
      },
    });
  }

  private saveMember(targetUserId: number): void {
    const { userId, menuId } = this.auth();
    if (!userId || !menuId) return;
    this.isSaving.set(true);
    this.api.saveStampGroupMember({
      userId, menuId, id: 0, groupId: this.detail.id,
      targetUserId,
      active: true,
      hasLimit: null,
      limitType: null,
      limitQty: null,
    }).subscribe({
      next: (res) => this.handleSaveResponse(res, () => {
        this.loadDetail();
        this.loadLogs();
        if (this.showUserPicker) this.loadUserPickerRows();
      }),
      error: (err) => { this.isSaving.set(false); this.toast.error(err?.error?.message || 'บันทึก member ไม่สำเร็จ'); },
    });
  }

  private auth(): { userId: number; menuId: number } {
    const userId = Number(this.cookieService.get('user_id') || 0);
    const menuId = this.permissionService.getMenuId(this.router.url) || 0;
    if (!userId || !menuId) this.toast.error('ไม่พบข้อมูลสิทธิ์');
    return { userId, menuId };
  }

  private handleSaveResponse(res: unknown, onSuccess: () => void): void {
    const mapped = this.masterMapper.mapActionResponse(res as never);
    this.isSaving.set(false);
    if (mapped.isSuccess) { this.toast.success(mapped.message || 'บันทึกสำเร็จ'); onSuccess(); }
    else this.toast.error(mapped.message || 'บันทึกไม่สำเร็จ');
  }

  private loadRateOptions(): void {
    this.api.getStampDiscountRateOptions(this.siteId || this.detail?.siteId).subscribe({
      next: (res) => {
        this.rateOptions = this.mapper.mapRateOptions(res);
        this.refreshRatePickerOptions();
      },
      error: () => { this.rateOptions = []; this.ratePickerOptions.set([]); },
    });
  }

  private loadCustomerOptions(searchText: string): void {
    this.customerPickerLoading.set(true);
    this.api.getStampCustomerOptions(searchText || undefined).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapCustomerOptions(res);
        this.customerPickerOptions.set(mapped.data.map((c) => ({ id: c.id, label: c.label })));
        this.customerPickerTotal.set(mapped.total);
        this.customerPickerLoading.set(false);
      },
      error: () => {
        this.customerPickerOptions.set([]);
        this.customerPickerTotal.set(0);
        this.customerPickerLoading.set(false);
      },
    });
  }

  private refreshRatePickerOptions(): void {
    const used = new Set(this.detail?.lines?.map((l) => l.parkingRateId) ?? []);
    const filtered = this.rateOptions
      .filter((r) => !used.has(r.id))
      .filter((r) => {
        if (!this.rateSearchText) return true;
        const label = `${r.rateId ?? ''} ${r.name ?? ''}`.toLowerCase();
        return label.includes(this.rateSearchText);
      });
    const preview = filtered.slice(0, ModalStampGroupComponent.PREVIEW);
    this.ratePickerOptions.set(
      preview.map((r) => ({
        id: r.id,
        label: `${r.rateId ?? ''} — ${r.name ?? ''}`.trim(),
        subLabel: r.siteId ? `site #${r.siteId}` : undefined,
      })),
    );
    this.ratePickerTotal.set(filtered.length);
  }

  private loadDetail(closeOnError = true): void {
    this.isLoading.set(true);
    this.api.getStampGroupById(this.groupId || this.detail.id).subscribe({
      next: (res) => {
        this.detail = this.mapper.mapGroupDetail(res);
        this.groupId = this.detail.id;
        this.refreshRatePickerOptions();
        if (this.detail.costType === 'billable') {
          this.loadCustomerOptions('');
        }
        this.isLoading.set(false);
        this.loadLogs();
      },
      error: () => {
        this.isLoading.set(false);
        this.toast.error('โหลดไม่สำเร็จ');
        if (closeOnError) this.onClose();
      },
    });
  }

  private loadLogs(): void {
    if (!this.detail?.id) { this.logs = []; return; }
    this.api.getStampGroupLogs(this.detail.id, 1, 50).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapGroupLogs(res);
        this.logs = mapped.data;
        this.logsTotal = mapped.total;
      },
      error: () => { this.logs = []; this.logsTotal = 0; },
    });
  }

  private loadUserPickerRows(): void {
    this.userPickerLoading.set(true);
    this.api.getStampUserOptions({
      page: this.userPickerPage,
      pageSize: this.userPickerPageSize,
      searchText: this.userPickerSearch || undefined,
      excludeGroupId: this.detail.id || null,
    }).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapUserOptionsTable(res);
        this.userPickerRows = mapped.data;
        this.userPickerTotal = mapped.total;
        this.userPickerPage = mapped.page;
        this.userPickerLoading.set(false);
      },
      error: () => {
        this.userPickerRows = [];
        this.userPickerTotal = 0;
        this.userPickerLoading.set(false);
      },
    });
  }

  private emptyDetail(): StampGroupDetail {
    return {
      id: 0, name: '', siteId: null, siteName: '', active: true, costType: 'free',
      cpCustomerId: null, cpCustomerName: null, hasLimit: false, limitType: null, limitQty: null,
      remark: '', lines: [], members: [],
    };
  }

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;

  get canShowActivityHistory(): boolean {
    return (this.detail?.id ?? this.groupId ?? 0) > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }

  get activityHistoryEntityId(): number {
    return this.detail?.id || this.groupId || 0;
  }
}
